import React, { useState } from 'react'
import { Box, IconButton, InputBase, TextField, Tooltip, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { AddTarget, ConditionRow, JoinSelect } from './builderParts'
import { containerEdge } from './surface'
import {
  appendConditions,
  groupCodes,
  isAnyAnswer,
  onePerAnswer,
  removeCondition,
  ruleOf,
  setJoin,
  setRule,
  toRows,
  type Condition,
  type Group,
} from './builderModel'
import type { Category } from './useBuilderData'

// GroupsBuilder is the Codes tab: each group is one code (a construct line),
// as in Ruby's Construct Variables grid (Code | Filter), and one category of
// the new variable. A line's header shows the code it is saved with and its
// label; below come its conditions as cards, with an "and" / "or" between
// each pair ("and" binds tighter than "or", Carbon's own reading of "a&b|c",
// so a group is a set of or-rows of ands underneath), and then its rule as
// text, always shown and editable. Cards and text are two views of one rule:
// editing either updates the other. A rule the cards cannot draw is kept, and
// saved, as the text.
//
// What is ticked in the tree (`selection`) goes in through a green +: at the
// end of a line, as a new line, or (one whole question ticked) one line per
// answer.
export interface GroupsBuilderProps {
  groups: Group[]
  onChange: (groups: Group[]) => void
  categoriesOf: (name: string) => Category[] | undefined
  labelOf: (variable: string, code: string) => string
  variableLabel: (name: string) => string
  onOpenVariable: (name: string) => void
  // The tree's ticked variables and answers, as conditions (and-ed).
  selection: Condition[]
  // Called once the selection has been added somewhere, to clear the ticks.
  onSelectionUsed: () => void
}

// mixesAndOr: the group has an "or" and a row of several "and"s, so the
// reader needs to know which binds first.
const mixesAndOr = (conditions: Condition[]): boolean => {
  const rows = toRows(conditions)
  return rows.length > 1 && rows.some(r => r.length > 1)
}

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace'

// RuleField is a line's rule as text. While it is being typed in, it shows
// the text as typed (so a rule the cards redraw, e.g. "region(1;2;3)" as
// "region(1/3)", does not jump under the cursor); on leaving it, it shows the
// rule as it will be saved.
const RuleField: React.FC<{ rule: string, label: string, onChange: (text: string) => void }> = ({ rule, label, onChange }) => {
  const [draft, setDraft] = useState<string | null>(null)
  return (
    <TextField
      size="small"
      fullWidth
      label="Rule"
      value={draft ?? rule}
      placeholder="e.g. gender(1)&region(1;3)"
      onChange={e => { setDraft(e.target.value); onChange(e.target.value) }}
      onBlur={() => setDraft(null)}
      helperText={<>Carbon syntax: <b>&amp;</b> and, <b>|</b> or, <b>!</b> not, brackets to group; codes <b>1;3</b> a list, <b>1/3</b> a range</>}
      inputProps={{ 'aria-label': label, spellCheck: false }}
      InputProps={{ sx: { fontFamily: MONO, fontSize: 13, bgcolor: 'background.paper' } }}
    />
  )
}

export const GroupsBuilder: React.FC<GroupsBuilderProps> = ({
  groups, onChange, categoriesOf, labelOf, variableLabel, onOpenVariable, selection, onSelectionUsed,
}) => {
  const update = (index: number, group: Group) => onChange(groups.map((g, i) => (i === index ? group : g)))
  const hasSelection = selection.length > 0
  const codes = groupCodes(groups)

  // "One code per answer" is offered for exactly one whole ticked question
  // that has answers (a numeric question has none to split into).
  const whole = selection.length === 1 && isAnyAnswer(selection[0].codes) ? selection[0] : null
  const wholeAnswers = whole ? categoriesOf(whole.variable) : undefined
  const canSplit = Boolean(whole && wholeAnswers && wholeAnswers.length > 0)

  const addToGroup = (index: number) => {
    update(index, appendConditions(groups[index], selection))
    onSelectionUsed()
  }
  const addAsNewGroup = () => {
    onChange([...groups, appendConditions({ label: '', conditions: [] }, selection)])
    onSelectionUsed()
  }
  const addOnePerAnswer = () => {
    if (!whole || !wholeAnswers) return
    onChange([...groups, ...onePerAnswer(whole.variable, wholeAnswers)])
    onSelectionUsed()
  }

  return (
    <Box sx={{ display: 'grid', gap: 1.5 }}>
      {groups.length === 0 && !hasSelection && (
        <Typography variant="body2" color="text.secondary" sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 2, p: 2 }}>
          No codes yet. Tick a question or some of its answers in the tree, then press the green + to add a code. Each code becomes one value of the new variable.
        </Typography>
      )}

      {groups.map((group, gi) => {
        const rule = ruleOf(group)
        const isRawRule = group.rawRule !== undefined && group.conditions.length === 0
        const setConditions = (conditions: Condition[]) => update(gi, { ...group, conditions })
        const code = codes[gi]
        return (
          <Box
            key={gi}
            data-testid={`group-${gi}`}
            // The line is a container: a visible edge and a faint tint, so the
            // white condition cards inside read as its items.
            sx={{
              border: '1px solid',
              borderColor: containerEdge,
              borderRadius: 2,
              p: 2,
              display: 'grid',
              gap: 1,
              bgcolor: 'action.hover',
              minWidth: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Tooltip title={code === null ? 'Gets its number once it has a rule' : 'The code number this gets in the new variable'}>
                <Box
                  data-testid={`group-${gi}-code`}
                  aria-label={code === null ? 'No code yet' : `Code ${code}`}
                  sx={{
                    minWidth: 28,
                    height: 28,
                    px: 0.75,
                    borderRadius: 1,
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: containerEdge,
                    color: code === null ? 'text.disabled' : 'text.primary',
                    fontFamily: MONO,
                    fontSize: 13,
                    fontWeight: 600,
                    fontVariantNumeric: 'tabular-nums',
                    flexShrink: 0,
                  }}
                >
                  {code ?? '–'}
                </Box>
              </Tooltip>
              <InputBase
                value={group.label}
                placeholder="Label, e.g. Men in London"
                onChange={e => update(gi, { ...group, label: e.target.value })}
                inputProps={{ 'aria-label': `Code ${gi + 1} label` }}
                sx={{ flex: 1, fontSize: 15, fontWeight: 600, '& input': { py: 0.25 } }}
              />
              <Tooltip title="Remove code">
                <IconButton size="small" aria-label={`Remove code ${gi + 1}`} onClick={() => onChange(groups.filter((_, i) => i !== gi))}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {isRawRule ? (
              <Typography variant="body2" color="text.secondary" data-testid={`group-${gi}-too-complex`}>
                This rule is too complex to show as conditions; edit it as text.
              </Typography>
            ) : (
              <>
                {group.conditions.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Tick a question (any answer) or some of its answers in the tree, then press this code's green + to add them, or type a rule below.
                  </Typography>
                )}
                {group.conditions.length > 0 && (
                  <Box data-testid={`group-${gi}-conditions`} sx={{ display: 'grid', minWidth: 0 }}>
                    {group.conditions.map((c, ci) => (
                      <React.Fragment key={ci}>
                        {ci > 0 && (
                          <Box sx={{ pl: 1.5, py: 0.75 }}>
                            <JoinSelect
                              join={c.or ? 'or' : 'and'}
                              onChange={join => setConditions(setJoin(group.conditions, ci, join))}
                              label={`Code ${gi + 1}: and or or before condition ${ci + 1}`}
                            />
                          </Box>
                        )}
                        <ConditionRow
                          condition={c}
                          question={variableLabel(c.variable)}
                          categories={categoriesOf(c.variable)}
                          labelOf={answer => labelOf(c.variable, answer)}
                          onOpenVariable={() => onOpenVariable(c.variable)}
                          onChange={next => setConditions(group.conditions.map((x, i) => (i === ci ? next : x)))}
                          onDelete={() => setConditions(removeCondition(group.conditions, ci))}
                        />
                      </React.Fragment>
                    ))}
                  </Box>
                )}
                {mixesAndOr(group.conditions) && (
                  <Typography variant="caption" color="text.secondary">
                    “and” is applied before “or”: A and B or C means (A and B) or C.
                  </Typography>
                )}
              </>
            )}

            <RuleField rule={rule} label={`Code ${gi + 1} rule`} onChange={text => update(gi, setRule(group, text))} />

            {hasSelection && !isRawRule && (
              <AddTarget label={`Add to code ${gi + 1}`} text="Add to this code" onClick={() => addToGroup(gi)} />
            )}
          </Box>
        )
      })}

      {hasSelection && (
        <Box sx={{ justifySelf: 'start', display: 'flex', flexWrap: 'wrap', gap: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 2, px: 1.5, py: 1 }}>
          <AddTarget label="Add as a new code" text="Add as a new code" onClick={addAsNewGroup} />
          {canSplit && (
            <AddTarget label="One code per answer" text="One code per answer" onClick={addOnePerAnswer} />
          )}
        </Box>
      )}
    </Box>
  )
}

export default GroupsBuilder
